/**
 * PlaylistList Component
 * Virtualized list of user playlists with search filtering
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, Music } from 'lucide-react';
import { useInfinitePlaylists, useLikedTracks } from '../../api/hooks';
import { trackService } from '../../api/services/tracks';
import { PlaylistItem } from './PlaylistItem';
import { useWorkbenchStore } from '../../stores/workbench';
import { useAuth } from '../../features/auth/AuthProvider';
import type { PlaylistSummary, PlaylistDetail } from '../../api/types';

type SortOrder = 'name-asc' | 'name-desc' | 'recent';

interface PlaylistListProps {
  searchQuery: string;
  isWideMode: boolean;
  sortOrder: SortOrder;
}

const PINNED_PLAYLISTS_KEY = 'splister:pinnedPlaylists';

/**
 * Load pinned playlists from localStorage
 */
function loadPinnedPlaylists(): Set<string> {
  try {
    const stored = localStorage.getItem(PINNED_PLAYLISTS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Save pinned playlists to localStorage
 */
function savePinnedPlaylists(pinned: Set<string>): void {
  try {
    localStorage.setItem(PINNED_PLAYLISTS_KEY, JSON.stringify([...pinned]));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

export function PlaylistList({ searchQuery, isWideMode, sortOrder }: PlaylistListProps) {
  const { isAuthenticated } = useAuth();

  // Infinite query for playlists with pagination
  // Gated on isAuthenticated so this doesn't fire before a valid session exists
  // (e.g. transient window right after the OAuth callback screen mounts).
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useInfinitePlaylists(50, { enabled: isAuthenticated });

  const { data: likedTracksData, isLoading: isLoadingLiked, error: likedError } = useLikedTracks(
    { limit: 1 }, // Fetch just metadata
    { enabled: isAuthenticated }
  );
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(loadPinnedPlaylists);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);
  const setActivePlaylist = useWorkbenchStore((state) => state.setActivePlaylist);

  // Flatten all pages into single array
  const allPlaylists = data?.pages.flatMap((page) => page.playlists) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  // Load all pages when user starts searching (so search covers all playlists)
  useEffect(() => {
    if (searchQuery.trim() && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [searchQuery, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Load all pages when sort order changes to name-based sorting
  // (so alphabetical sorting covers all playlists, not just loaded ones)
  useEffect(() => {
    if (sortOrder !== 'recent' && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [sortOrder, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Debug logging
  React.useEffect(() => {
    console.log('PlaylistList - Liked tracks data:', likedTracksData);
    console.log('PlaylistList - Liked tracks loading:', isLoadingLiked);
    console.log('PlaylistList - Liked tracks error:', likedError);
  }, [likedTracksData, isLoadingLiked, likedError]);

  // Persist pinned state changes
  useEffect(() => {
    savePinnedPlaylists(pinnedIds);
  }, [pinnedIds]);

  // Reset expanded state when switching to dense mode
  useEffect(() => {
    if (!isWideMode) {
      setExpandedIds(new Set());
    }
  }, [isWideMode]);

  // Toggle expansion state
  const handleToggleExpand = useCallback((playlistId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(playlistId)) {
        next.delete(playlistId);
      } else {
        next.add(playlistId);
      }
      return next;
    });
  }, []);

  // Filter and sort playlists
  const filteredPlaylists = useMemo(() => {
    const playlists: PlaylistSummary[] = [];

    // Create Liked Songs pseudo-playlist if we have data
    if (likedTracksData) {
      const likedSongsPlaylist: PlaylistSummary = {
        id: 'liked-songs',
        name: 'Liked Songs',
        description: 'Your saved tracks',
        tracksCount: likedTracksData.total,
        owner: 'You',
        public: false,
        collaborative: false,
        snapshotId: '',
        uri: 'spotify:collection:tracks',
        images: [],
      };

      // Add Liked Songs to the list if it matches search query
      const normalizedQuery = searchQuery.toLowerCase().trim();
      if (!normalizedQuery || likedSongsPlaylist.name.toLowerCase().includes(normalizedQuery)) {
        playlists.push(likedSongsPlaylist);
      }
    }

    // Add regular playlists from all pages
    if (allPlaylists.length > 0) {
      // Filter by search query
      const normalizedQuery = searchQuery.toLowerCase().trim();
      const filtered = normalizedQuery
        ? allPlaylists.filter(p =>
            p.name.toLowerCase().includes(normalizedQuery)
          )
        : allPlaylists;

      // Sort regular playlists based on sortOrder
      const sorted = filtered.sort((a, b) => {
        const aPinned = pinnedIds.has(a.id);
        const bPinned = pinnedIds.has(b.id);

        // Pinned playlists always come first
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;

        // Apply sort order for non-pinned (or both pinned)
        if (sortOrder === 'name-asc') {
          return a.name.localeCompare(b.name);
        } else if (sortOrder === 'name-desc') {
          return b.name.localeCompare(a.name);
        }
        // 'recent': keep original Spotify order (don't sort)
        return 0;
      });

      playlists.push(...sorted);
    }

    return playlists;
  }, [allPlaylists, likedTracksData, searchQuery, pinnedIds, sortOrder]);

  // Toggle pin state
  const handleTogglePin = useCallback((playlistId: string) => {
    // Don't allow pinning Liked Songs
    if (playlistId === 'liked-songs') return;

    setPinnedIds(prev => {
      const next = new Set(prev);
      if (next.has(playlistId)) {
        next.delete(playlistId);
      } else {
        next.add(playlistId);
      }
      return next;
    });
  }, []);

  // Handle playlist click - load into workbench
  const handlePlaylistClick = useCallback(async (playlist: PlaylistSummary) => {
    // Check for unsaved changes before navigating away
    const isDirty = useWorkbenchStore.getState().isDirty;

    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes in the current playlist. Switching playlists will discard these changes.\n\nDo you want to continue?'
      );
      if (!confirmed) {
        return; // User cancelled - stay on current playlist
      }
    }

    // Special handling for Liked Songs
    if (playlist.id === 'liked-songs') {
      try {
        // Fetch liked tracks from the correct endpoint
        // Note: Backend limits to 50 tracks per request. TODO: Implement pagination for larger collections
        const likedData = await trackService.getLiked({ limit: 50, offset: 0 });

        const detail: PlaylistDetail = {
          id: 'liked-songs',
          name: 'Liked Songs',
          description: 'Your saved tracks',
          public: false,
          collaborative: false,
          owner: {
            id: 'spotify',
            displayName: 'Spotify',
            uri: 'spotify:user:spotify',
          },
          snapshotId: '',
          uri: 'spotify:collection:tracks',
          images: [],
          tracks: {
            total: likedData.tracks.length,
            items: likedData.tracks.map((track) => ({
              track,
              addedAt: new Date().toISOString(), // Liked tracks don't have addedAt in the response
              addedBy: { id: 'spotify', displayName: 'You', uri: 'spotify:user:spotify' },
            })),
          },
        };

        setActivePlaylist(detail);
      } catch (error) {
        console.error('Failed to load Liked Songs:', error);
        alert('Failed to load Liked Songs. Please try again.');
      }
      return;
    }

    // Convert summary to detail format (will be replaced by actual fetch)
    // The usePlaylist hook will be called internally when needed
    const detail: PlaylistDetail = {
      id: playlist.id,
      name: playlist.name,
      description: playlist.description,
      public: playlist.public,
      collaborative: playlist.collaborative,
      owner: {
        id: playlist.owner,
        displayName: playlist.owner,
        uri: `spotify:user:${playlist.owner}`,
      },
      snapshotId: playlist.snapshotId,
      uri: playlist.uri,
      images: playlist.images,
      tracks: {
        total: 0,
        items: [], // Will be fetched
      },
    };

    setActivePlaylist(detail);
  }, [setActivePlaylist]);

  // Virtualization setup
  const parentRef = React.useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filteredPlaylists.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56, // Base height of collapsed item
    overscan: 10, // Render extra items for smooth scrolling
    // Enable dynamic measurement for expanded playlists
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  });

  // Auto-fetch next page when scrolling near bottom
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      // Only auto-fetch if we're not filtering (search query is empty)
      if (searchQuery.trim()) return;
      if (!hasNextPage || isFetchingNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      // Fetch when within 200px of bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    // Check immediately in case we're already near bottom
    handleScroll();

    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, searchQuery]);

  // Loading state
  if (status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
        <p className="text-sm text-zinc-400">Loading playlists...</p>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center p-4">
        <Music className="w-12 h-12 text-red-500/50 mb-3" />
        <p className="text-sm text-red-400 mb-1">Failed to load playlists</p>
        <p className="text-xs text-zinc-500">{error.message}</p>
      </div>
    );
  }

  // Empty state
  if (filteredPlaylists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center p-4">
        <Music className="w-12 h-12 text-zinc-700 mb-3" />
        {searchQuery ? (
          <>
            <p className="text-sm text-zinc-400 mb-1">No playlists found</p>
            <p className="text-xs text-zinc-600">Try a different search term</p>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-400 mb-1">No playlists yet</p>
            <p className="text-xs text-zinc-600">Create a playlist in Spotify to get started</p>
          </>
        )}
      </div>
    );
  }

  // Virtualized list
  return (
    <div ref={parentRef} className="flex-1 overflow-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const playlist = filteredPlaylists[virtualItem.index];
          if (!playlist) return null;
          const isExpanded = expandedIds.has(playlist.id);

          return (
            <div
              key={`${playlist.id}-${isExpanded}`}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <PlaylistItem
                playlist={playlist}
                isWideMode={isWideMode}
                isActive={activePlaylist?.id === playlist.id}
                isPinned={pinnedIds.has(playlist.id)}
                isExpanded={isExpanded}
                onTogglePin={handleTogglePin}
                onToggleExpand={handleToggleExpand}
                onClick={handlePlaylistClick}
              />
            </div>
          );
        })}
      </div>

      {/* Loading indicator for next page */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin mr-2" />
          <span className="text-xs text-zinc-400">Loading more playlists...</span>
        </div>
      )}

      {/* End of list indicator */}
      {!hasNextPage && allPlaylists.length > 0 && !searchQuery.trim() && (
        <div className="flex items-center justify-center py-4">
          <span className="text-xs text-zinc-500">
            All {total} playlists loaded
          </span>
        </div>
      )}
    </div>
  );
}
